import { AsyncPipe, NgClass } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, computed, inject, input, OnInit, signal } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  CardListComponent,
  ErrorAlertComponent,
  NkCardListConfig,
  NkCardListData,
  ProgressComponent,
} from '@nk/ui-lib';
import { LOADING_STATE_HANDLER_SERVICE_TOKEN, Sorting } from '@nk/utils-lib';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { catchError, finalize, map, switchMap, tap } from 'rxjs/operators';
import { ScreeningCaseService } from '../../services/screening-case.service';
import { ScreeningInputParamsService } from '../../services/screening-input-params.service';
import { ScreeningEntitiesInputParams } from '../../utils/screening-entities.utils';
import {
  hasNewUnhandledCases,
  SCREENING_CASE_CARD_LIST_CONFIG,
  ScreeningCaseCardListItem,
  transformToScreeningCaseCardListItems,
} from './screening-case-card-list.utils';

const DEFAULT_PAGE_LIMIT = 100;

@Component({
  selector: 'nk-screening-case-card-list',
  imports: [AsyncPipe, NgClass, TranslateModule, CardListComponent, ErrorAlertComponent, ProgressComponent],
  templateUrl: './screening-case-card-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ScreeningCaseCardListComponent implements OnInit {
  public collapsibleDetails = input<boolean>(true);

  public cardListConfig: NkCardListConfig<ScreeningCaseCardListItem> = SCREENING_CASE_CARD_LIST_CONFIG;
  public data$!: Observable<NkCardListData<ScreeningCaseCardListItem>>;
  public isLoading = signal(false);
  public error = signal<HttpErrorResponse | null>(null);
  public hasNewUnhandledCases = signal(false);
  public hasScreeningParams = signal(false);
  private _screeningEntitiesInputParams$!: Observable<ScreeningEntitiesInputParams | null>;
  private _filters$ = new BehaviorSubject<{
    page: number;
    limit: number;
    sortTable: Sorting[];
  }>({ page: 0, limit: DEFAULT_PAGE_LIMIT, sortTable: [] });

  private _screeningCaseService = inject(ScreeningCaseService);
  private _screeningInputParamsService = inject(ScreeningInputParamsService);
  private _reportLoadingStateService = inject(LOADING_STATE_HANDLER_SERVICE_TOKEN, { optional: true });
  private _screeningCaseCardListLoadingId = computed(() => `${this.constructor.name}_screeningCaseCardList`);
  private _translateService = inject(TranslateService);

  public ngOnInit(): void {
    if (this._reportLoadingStateService) {
      this._reportLoadingStateService.setLoadingState(this._screeningCaseCardListLoadingId(), true);
    }
    this._screeningEntitiesInputParams$ = this._screeningInputParamsService.screeningEntitiesInputParams$;
    this.data$ = combineLatest([this._filters$, this._screeningEntitiesInputParams$]).pipe(
      tap(([, params]) => {
        this.hasScreeningParams.set(!!params?.screeningConfigUuid && !!params?.monitoringOriginEntityUuid);
        this.error.set(null);
      }),
      switchMap(([filters, screeningEntitiesInputParams]) => {
        if (
          !screeningEntitiesInputParams?.screeningConfigUuid ||
          !screeningEntitiesInputParams?.monitoringOriginEntityUuid
        ) {
          this.isLoading.set(false);
          return of({ entities: [], pagination: { limit: DEFAULT_PAGE_LIMIT, page: 0, totalCount: 0 } });
        }
        this.isLoading.set(true);
        const offset = filters.page * filters.limit;
        return this._screeningCaseService.screeningEntityScreeningCaseCompositeCollectionCache
          .get(
            screeningEntitiesInputParams.screeningConfigUuid,
            screeningEntitiesInputParams.monitoringOriginEntityUuid,
            undefined,
            filters.limit,
            offset,
          )
          .pipe(
            tap((payload) => {
              this.hasNewUnhandledCases.set(hasNewUnhandledCases(payload.elements));
            }),
            map((payload) => {
              const entities = transformToScreeningCaseCardListItems(payload.elements, this._translateService);
              return {
                entities,
                pagination: {
                  limit: filters.limit,
                  page: filters.page,
                  totalCount: entities.length,
                },
              };
            }),
            finalize(() => {
              this.isLoading.set(false);
              if (this._reportLoadingStateService) {
                this._reportLoadingStateService.setLoadingState(this._screeningCaseCardListLoadingId(), false);
              }
            }),
            catchError((error: HttpErrorResponse) => {
              this.error.set(error);
              this.isLoading.set(false);
              return [];
            }),
          );
      }),
    );
  }

  public onChangePage(page: number): void {
    const currentFilters = this._filters$.getValue();
    this._filters$.next({
      ...currentFilters,
      page,
    });
  }
}
